import React, { useEffect, useState, useRef } from 'react';
import './UserProfileDetails.css';
import axios from "../../../axiosInstance.js";
import { useNavigate, useLocation } from 'react-router-dom';
import { ArrowLeft, Camera, Pencil, Check, CheckCircle2, ChevronDown } from 'lucide-react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import CustomInput from './CustomInput';
import BottomNavigationCustomer from '../CustomerHome/BottomNavigatonCustomer';
import { useModal } from '../../../context/ModalContext';
import { State, City } from "country-state-city";

/* =========================================================
   TOP-LEVEL FIELD / HEADER COMPONENTS
   -----------------------------------------------------------
   These used to be declared *inside* UserProfileDetails, which
   meant React created a brand-new component type on every
   render. That forced React to unmount + remount the underlying
   <input> DOM node after every keystroke, so the field lost
   focus and you could only ever type one character at a time.

   Declaring them here, outside the component, keeps their
   identity stable across renders so the <input>/<select> DOM
   node persists and focus is preserved while typing.
========================================================= */

const GENDER_OPTIONS = [
    { value: 'Male', label: 'Male' },
    { value: 'Female', label: 'Female' },
    { value: 'Other', label: 'Other' },
];

const EditableField = ({
    label,
    value,
    onChangeValue,
    placeholder,
    isEditMode,
    onEnterSubmit,
    type = 'text',
    options = [],
}) => {
    if (!isEditMode) {
        // Read-only display. For a select field, show the friendly label
        // instead of the raw stored value.
        const displayValue = type === 'select'
            ? (options.find(opt => opt.value === value)?.label || value)
            : value;

        return (
            <div className="upd-info-row">
                <span className="upd-info-label">{label}</span>
                <span className="upd-info-value">{displayValue || '—'}</span>
            </div>
        );
    }

    if (type === 'select') {
        return (
            <div className="upd-info-row">
                <span className="upd-info-label">{label}</span>
                <div className="upd-info-select-wrap">
                    <select
                        className="upd-info-select"
                        value={value || ''}
                        onChange={(e) => onChangeValue(e.target.value)}
                    >
                        <option value="" disabled>{placeholder || 'Select'}</option>
                        {options.map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </select>
                    <ChevronDown size={14} className="upd-info-select-chevron" />
                </div>
            </div>
        );
    }

    return (
        <div className="upd-info-row">
            <span className="upd-info-label">{label}</span>
            <input
                className="upd-info-input"
                value={value || ''}
                placeholder={placeholder}
                onChange={(e) => onChangeValue(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); onEnterSubmit && onEnterSubmit(e); } }}
            />
        </div>
    );
};

const ProfileHeader = ({
    name,
    email,
    showBack,
    handleBack,
    isEditMode,
    handleEditToggle,
    isLoading,
    avatarSrc,
    handleProfileInputRefClick,
    profileInputRef,
    handleChange,
    tabs,
    activeTab,
    setActiveTab,
}) => (
    <div className="upd-header">
        {showBack && (
            <button className="upd-back-btn" onClick={handleBack} aria-label="Back">
                <ArrowLeft size={18} />
            </button>
        )}

        <button
            type="button"
            className={`upd-edit-toggle ${isEditMode ? 'saving' : ''}`}
            onClick={handleEditToggle}
            disabled={isLoading}
        >
            {isEditMode ? (
                <>
                    {isLoading ? <span className="upd-spinner" /> : <Check size={13} />}
                    {isLoading ? 'Saving' : 'Save'}
                </>
            ) : (
                <>
                    <Pencil size={13} />
                    Edit
                </>
            )}
        </button>

        <div className="upd-identity">
            <div className="upd-avatar-wrap">
                <img
                    className="upd-avatar"
                    src={avatarSrc}
                    alt=""
                    onClick={handleProfileInputRefClick}
                />
                <input
                    hidden
                    ref={profileInputRef}
                    type="file"
                    name="profileImage"
                    onChange={handleChange}
                    accept="image/*"
                    className="form-control"
                    required
                />
                <button
                    type="button"
                    className="upd-avatar-edit"
                    onClick={handleProfileInputRefClick}
                    aria-label="Change photo"
                >
                    <Camera size={13} />
                </button>
            </div>
            <h2 className="upd-name">{name}</h2>
            <p className="upd-email">{email}</p>
        </div>

        <div className="upd-tabs">
            {tabs.map((tab) => (
                <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`upd-tab ${activeTab === tab.id ? 'active' : ''}`}
                >
                    {tab.label}
                </button>
            ))}
        </div>
    </div>
);

const UserProfileDetails = () => {

    const navigate = useNavigate()
    const token = localStorage.getItem("token");
    const userId = localStorage.getItem("userId");
    const [alertInfo, setAlertInfo] = useState(null);
    const [comingData, setComingData] = useState([]);
    const { state } = useLocation();
    const [states, setStates] = useState([]);
    const [cities, setCities] = useState([]);
    const [isLoadingStates, setIsLoadingStates] = useState(true);
    const [isLoadingCities, setIsLoadingCities] = useState(true);
    const { openModal, modalData, closeModal } = useModal();

      const [formData, setFormData] = useState({

        customerDriverCode: "",
        vendorDriverCode: "",
        CustomerName: "",
        contactPerson: "",
        vendorPhone: "",
        email: '',
        CustomerPhone: '',
        plan: '',
        vehicles: '',
        CustomerType: '',
        choosenPlan: "",
        vendorType: '',
        panNo: '',
        GSTNo: '',
        adharNo: '',
        adharCard: '',
        panCard: '',
        GST: '',
        businessRegistration: '',
        firstView: true,
        vendorName: '',
        CustomerName: '',
        driverName: "",
        driverNumber: "",
        driverEmail: "",
        vehicleNo: '',
        chassisNo: '',
        engineNo: '',
        make: '',
        model: '',
        type: '',
        application: '',
        InsuranceName: '',
        GVW: '',
        ULW: '',
        DLCard: '',
        DLNo: '',
        year: '',
        bankName: '',
        bankAccount: '',
        ifscCode: '',
        branchName: '',
        cancelledCheque: '',
        alternatePhone: '',
        gender: '',
        dateOfBirth: ''

    });

    useEffect(() => {
        if (comingData) {

            setFormData(prevFormData => ({
                ...prevFormData,

                driverName: comingData.driverName || "",
                driverNumber: comingData.driverNumber || "",
                driverEmail: comingData.driverEmail || "",
                customerDriverCode: comingData.customerDriverCode || "",
                vendorDriverCode: comingData.vendorDriverCode || "",
                CustomerCode: comingData.CustomerCode || "",
                CustomerName: comingData.CustomerName || "",
                CustomerType: comingData.CustomerType || "",
                address: comingData.address || "",
                district: comingData.district || "",
                pincode: comingData.pincode || "",
                CustomerPhone: comingData.CustomerPhone || "",
                email: comingData.email || "",
                contactPerson: comingData.contactPerson || "",
                state: comingData.state || "",
                panNo: comingData.panNo || "",
                panCard: comingData.panCard || "",
                adharNo: comingData.adharNo || "",
                adharCard: comingData.adharCard || "",
                agreement: comingData.agreement || "",
                fleetSize: comingData.fleetSize || "",
                plan: comingData.plan || "",
                vehicleNo: comingData.vehicleNo || "",
                chassisNo: comingData.chassisNo || "",
                engineNo: comingData.engineNo || "",
                make: comingData.make || "",
                model: comingData.model || "",
                year: comingData.year || "",
                type: comingData.type || "",
                application: comingData.application || "",
                GVW: comingData.GVW || "",
                ULW: comingData.ULW || "",
                InsuranceName: comingData.InsuranceName || "",
                GSTNo: comingData.GSTNo || "",
                GST: comingData.GST || "",
                businessRegistration: comingData.businessRegistration || "",
                longitude: comingData.longitude !== null ? comingData.longitude : "",
                latitude: comingData.latitude !== null ? comingData.latitude : "",
                id: comingData.id,
                choosenPlan: comingData.choosenPlan,
                vendorPhone: comingData.vendorPhone || "",
                vendorType: comingData.vendorType || "",

                vendorName: comingData.vendorName || "",
                CustomerName: comingData.CustomerName || "",
                companyName: comingData.companyName || "",


                vehicleNo: comingData.vehicleNo || '',
                chassisNo: comingData.chassisNo || '',
                engineNo: comingData.engineNo || '',
                make: comingData.make || '',
                model: comingData.model || '',
                type: comingData.type || '',
                application: comingData.application || '',
                InsuranceName: comingData.InsuranceName || '',
                GVW: comingData.GVW || '',
                ULW: comingData.ULW || '',
                DLCard: comingData.DLCard || '',
                DLNo: comingData.DLNo || '',
                year: comingData.year || '',

                bankName: comingData.bankName || '',
                bankAccount: comingData.bankAccount || '',
                ifscCode: comingData.ifscCode || '',
                branchName: comingData.branchName || '',
                cancelledCheque: comingData.cancelledCheque || '',
                profileImage: comingData.profileImage || '',
                alternatePhone: comingData.alternatePhone || '',
                gender: comingData.gender || '',
                dateOfBirth: comingData.dateOfBirth || ''


            }));
        }
    }, [comingData]);

    useEffect(() => {
        // load available states on mount
        loadStates();
    }, []);

    useEffect(() => {
        // when state changes (including when comingData populates it), load cities
        if (formData.state) {
            setCities([]);
            loadCities(formData.state);
        } else {
            setCities([]);
        }
    }, [formData.state]);

    useEffect(() => {
        if(userId && token) {
            getDataById();
        }
    }, [userId, token])

    const getDataById = async (id) => {

        let response;
        if (userId?.startsWith("CUD-")) response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/getDriverInfo/${userId}`, { headers: { 'Authorization': `Bearer ${token}` } });
        if (userId?.startsWith("CC-")) response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/getCustomerById/${userId}/${userId}`, { headers: { 'Authorization': `Bearer ${token}` } });
        if (userId?.startsWith("VC-")) response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/getVendor/${userId}/${userId}`, { headers: { 'Authorization': `Bearer ${token}` } });
        if (userId?.startsWith("VED-")) response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/getVendorDriverInfo/${userId}`, { headers: { 'Authorization': `Bearer ${token}` } });

        setComingData(response.data?.data[0])

    }

    const loadStates = () => {
        setIsLoadingStates(true);
        try {
            const allStates = State.getStatesOfCountry("IN");
            setStates(allStates);
        } catch (error) {
            console.error("Error loading states:", error);
            setStates([]);
        } finally {
            setIsLoadingStates(false);
        }
    }

    const loadCities = (stateCode) => {
        setIsLoadingCities(true);
        try {
            const allCities = City.getCitiesOfState("IN", stateCode);
            setCities(allCities);
        } catch (error) {
            console.error("Error loading cities:", error);
            setCities([]);
        } finally {
            setIsLoadingCities(false);
        }
    };

    const showSuccessToast = (e) => {
        toast.success(e);
    };

    const showErrorToast = (e) => {
        toast.error(e);
    };

    const [isLoading, setIsLoading] = useState(false);

    const handleBack = () => {
        navigate(-1);
    }

    const [isUpdatePan, setIsUpdatePan] = useState(false)
    const [isUpdateBussinessReg, setIsUpdateBussinessReg] = useState(false)
    const [isUpdateCancelledCheque, setIsUpdateCancelledCheque] = useState(false)
    const [isBusinessRegistrationDoc, setIsBusinessRegistrationDoc] = useState(false)
    const [isUpdateAdhar, setIsUpdateAdhar] = useState(false)
    const [isUpdateGST, setIsUpdateGST] = useState(false)
    const [isUpdateDL, setIsUpdateDL] = useState(false)

    const [step, setStep] = useState(1);
    const nextStep = () => setStep(step + 1);
    const prevStep = () => setStep(step - 1);

    const tabs = [
        { id: "tab1", label: "My Profile", },
    ];
    const [activeTab, setActiveTab] = useState("tab1");

    // Single top-level edit toggle — replaces the old per-section
    // isPersonalDetailEditable / isVehicleDetailEditable / isCompanyDetailEditable /
    // isEditable flags. One Edit button at the top now controls every field on the page.
    const [isEditMode, setIsEditMode] = useState(false);

    const handleEditToggle = async () => {
        if (isEditMode) {
            await handleSubmit({ preventDefault: () => {} });
            setIsEditMode(false);
        } else {
            setIsEditMode(true);
        }
    };

    const profileInputRef = useRef(null);
    const panInputRef = useRef(null);
    const aadharInputRef = useRef(null);

    const handlePanInputRefClick = () => {
        if (panInputRef.current) {
            panInputRef.current.click();
        }
    };

    const handleAdharInputRefClick = () => {
        if (aadharInputRef.current) {
            aadharInputRef.current.click();
        }
    };

    const [profileFile, setProfileFile] = useState(null)
    const [panFile, setPanFile] = useState(null)
    const [aadharFile, setAadharFile] = useState(null)

    const handleChange = (e) => {
        const { name, value, type, files } = e.target;

        if (type === 'file') {
            if (files[0] && files[0].size > 9097152) {
                setAlertInfo({ show: true, message: 'File size should be less than 2 MB!', severity: 'error' });
            }
            setFormData(prevState => ({
                ...prevState,
                [name]: files[0]
            }));

            readAsDataURL(e)

        }
        else if (name === 'state') {
            setFormData(prevState => ({ ...prevState, [name]: value, district: '' }));
            setCities([]);
            if (value) loadCities(value);
        }
        else setFormData({ ...formData, [name]: value });

    };


    const readAsDataURL = (e) => {
        const file = e.target.files[0];

        if (file) {

            const reader = new FileReader();
            reader.onloadend = () => {

                if (e.target.name === 'profileImage') {
                    setProfileFile(
                        reader.result
                    );
                }

                if (e.target.name === 'panCard') {
                    setPanFile(
                        reader.result
                    );
                }

                if (e.target.name === 'adharCard') {
                    setAadharFile(
                        reader.result
                    );
                }
            };

            reader.readAsDataURL(file);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (formData.panNo === undefined || formData.panNo.trim() === "" || formData.panNo.startsWith("No Pan No")) {
            // panNo is mandatory — left as-is from original (validation currently no-ops here)
        } else if (!/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/.test(formData.panNo.trim().toUpperCase())) {
            openModal({
                title: 'Error',
                body: 'Invalid PAN number format.',
                iconType: 'error',
                autoClose: true,
            });
            return;
        }

        setIsLoading(true)

        const formDataObj = new FormData();
        for (const key in formData) {
            if (formData[key] !== undefined && formData[key] !== null && formData[key] !== "") {

                if (formData[key] instanceof File) {
                    formDataObj.append(key, formData[key], formData[key].name);
                } else {
                    formDataObj.append(key, formData[key]);
                }
            }
        }
        let urlPart = ''
        if (userId?.startsWith("CC-")) urlPart = 'customerUpdate';
        if (userId?.startsWith("CUD-")) urlPart = 'updateCustomerDriver';
        if (userId?.startsWith("VC-")) urlPart = 'venderUpdate';
        if (userId?.startsWith("VED-")) urlPart = 'updateDriverInfoVendor';

        try {

            const response = await axios({
                method: 'PUT',
                url: `${process.env.REACT_APP_BACKEND_URL}/api/${urlPart}/${userId}/${userId}`,
                data: formDataObj,
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            });

            if (response.data.status === true) {
                showSuccessToast('Information updated successfully!')
                await getDataById();

            } else if (response.data.status === 400) {
                showErrorToast('Already assigned Number!')
            }
        } catch (error) {
            console.error("Error:", error);

            if (error.response) {
                if (error.response.status === 404) {
                    showErrorToast('Resource not found!')
                } else if (error.response.status === 400) {
                    showErrorToast('Already assigned Number!')
                } else {
                    showErrorToast(`Unexpected error: ${error.response.status}`)
                }
            } else if (error.request) {
                showErrorToast('No response from the server. Please try again!')
            } else {
                showErrorToast('An error occurred while making the request!')
            }
        }
        finally {
            setIsLoading(false)
        }

    };

    const handleProfileInputRefClick = () => {
        if (profileInputRef.current) {
            profileInputRef.current.click();
        }
    };

    return (
        <div className="upd-page font-satoshi">

            {/* CC block */}
            {userId?.startsWith("CC-") && (
                <>
                    <div className="upd-simple-header">
                        <button className="upd-simple-back" onClick={handleBack} aria-label="Back">
                            <ArrowLeft size={18} />
                        </button>
                        <h1 className="upd-simple-title">Personal Details</h1>
                        <button
                            type="button"
                            className="upd-simple-edit"
                            onClick={handleEditToggle}
                            disabled={isLoading}
                        >
                            {isEditMode ? (isLoading ? 'Saving...' : 'Save') : 'Edit'}
                        </button>
                    </div>

                    <div className="upd-content">

                        {/* Personal Information — read-only rows that become editable in Edit mode */}
                        <div className="upd-info-card">
                            <p className="upd-info-title">Personal Information</p>

                            <EditableField
                                label="Full Name"
                                value={formData.CustomerName}
                                onChangeValue={(v) => setFormData({ ...formData, CustomerName: v })}
                                placeholder="Enter full name"
                                isEditMode={isEditMode}
                                onEnterSubmit={handleSubmit}
                            />
                            <EditableField
                                label="Primary Phone"
                                value={formData.CustomerPhone}
                                onChangeValue={(v) => setFormData({ ...formData, CustomerPhone: v })}
                                placeholder="Enter phone number"
                                isEditMode={isEditMode}
                                onEnterSubmit={handleSubmit}
                            />
                            <EditableField
                                label="Alternate Phone"
                                value={formData.alternatePhone}
                                onChangeValue={(v) => setFormData({ ...formData, alternatePhone: v })}
                                placeholder="Enter alternate number"
                                isEditMode={isEditMode}
                                onEnterSubmit={handleSubmit}
                            />
                            <EditableField
                                label="Email"
                                value={formData.email}
                                onChangeValue={(v) => setFormData({ ...formData, email: v })}
                                placeholder="Enter email"
                                isEditMode={isEditMode}
                                onEnterSubmit={handleSubmit}
                            />
                            <EditableField
                                label="Gender"
                                value={formData.gender}
                                onChangeValue={(v) => setFormData({ ...formData, gender: v })}
                                placeholder="Select gender"
                                isEditMode={isEditMode}
                                type="select"
                                options={GENDER_OPTIONS}
                            />
                            <EditableField
                                label="Date of Birth"
                                value={formData.dateOfBirth}
                                onChangeValue={(v) => setFormData({ ...formData, dateOfBirth: v })}
                                placeholder="YYYY-MM-DD"
                                isEditMode={isEditMode}
                                onEnterSubmit={handleSubmit}
                            />
                        </div>

                        {/* Company Detail */}
                        <div className="upd-info-card">
                            <p className="upd-info-title">Company Detail</p>

                            <EditableField
                                label="Company Name"
                                value={formData.CustomerName}
                                onChangeValue={(v) => setFormData({ ...formData, CustomerName: v })}
                                placeholder="Enter name"
                                isEditMode={isEditMode}
                                onEnterSubmit={handleSubmit}
                            />
                            <EditableField
                                label="Contact No."
                                value={formData.CustomerPhone}
                                onChangeValue={(v) => setFormData({ ...formData, CustomerPhone: v })}
                                placeholder="Enter number"
                                isEditMode={isEditMode}
                                onEnterSubmit={handleSubmit}
                            />
                            <EditableField
                                label="Plan"
                                value={formData.choosenPlan}
                                onChangeValue={(v) => setFormData({ ...formData, choosenPlan: v })}
                                placeholder="Enter plan"
                                isEditMode={isEditMode}
                                onEnterSubmit={handleSubmit}
                            />
                            <EditableField
                                label="Customer Type"
                                value={formData.CustomerType}
                                onChangeValue={(v) => setFormData({ ...formData, CustomerType: v })}
                                placeholder="Enter type"
                                isEditMode={isEditMode}
                                onEnterSubmit={handleSubmit}
                            />
                        </div>

                        {/* Company Address */}
                        <div className="upd-info-card">
                            <p className="upd-info-title">Company Address</p>

                            <EditableField
                                label="Address"
                                value={formData.address}
                                onChangeValue={(v) => setFormData({ ...formData, address: v })}
                                placeholder="Enter address"
                                isEditMode={isEditMode}
                                onEnterSubmit={handleSubmit}
                            />
                            <EditableField
                                label="City"
                                value={formData.city}
                                onChangeValue={(v) => setFormData({ ...formData, city: v })}
                                placeholder="Enter city"
                                isEditMode={isEditMode}
                                onEnterSubmit={handleSubmit}
                            />
                        </div>

                    </div>
                    <BottomNavigationCustomer />
                </>
            )}

            {/* CUD block */}
            {userId?.startsWith("CUD-") && (
                <>
                    <ProfileHeader
                        name={formData.CustomerName}
                        email={formData.email}
                        showBack={state?.frompage === 'craneuserprofile'}
                        handleBack={handleBack}
                        isEditMode={isEditMode}
                        handleEditToggle={handleEditToggle}
                        isLoading={isLoading}
                        avatarSrc={profileFile || formData.profileImage}
                        handleProfileInputRefClick={handleProfileInputRefClick}
                        profileInputRef={profileInputRef}
                        handleChange={handleChange}
                        tabs={tabs}
                        activeTab={activeTab}
                        setActiveTab={setActiveTab}
                    />

                    <div className="upd-content">

                        {/* Vehicle details */}
                        <div className="upd-section">
                            <p className="upd-section-title">Vehicle Details</p>
                            <div className="upd-fields-grid">
                                <div className="upd-field">
                                    <span className="upd-field-label">Vehicle No</span>
                                    <span className="upd-field-static">{formData.vehicleNo || 'N/A'}</span>
                                </div>
                                <div className="upd-field">
                                    <span className="upd-field-label">Chassis No</span>
                                    <span className="upd-field-static">{formData.chassisNo}</span>
                                </div>
                                <div className="upd-field">
                                    <span className="upd-field-label">Engine No</span>
                                    <span className="upd-field-static">{formData.engineNo}</span>
                                </div>
                                <div className="upd-field">
                                    <span className="upd-field-label">Make</span>
                                    <span className="upd-field-static">{formData.make}</span>
                                </div>
                                <div className="upd-field">
                                    <span className="upd-field-label">Model</span>
                                    <span className="upd-field-static">{formData.model}</span>
                                </div>
                                <div className="upd-field">
                                    <span className="upd-field-label">Year</span>
                                    <span className="upd-field-static">{formData.year}</span>
                                </div>
                                <div className="upd-field">
                                    <span className="upd-field-label">Type</span>
                                    <span className="upd-field-static">{formData.type}</span>
                                </div>
                                <div className="upd-field">
                                    <span className="upd-field-label">Application No</span>
                                    <span className="upd-field-static">{formData.application}</span>
                                </div>
                                <div className="upd-field">
                                    <span className="upd-field-label">Insurance</span>
                                    <span className="upd-field-static">{formData.InsuranceName}</span>
                                </div>
                                <div className="upd-field">
                                    <span className="upd-field-label">GVW</span>
                                    <span className="upd-field-static">{formData.GVW}</span>
                                </div>
                                <div className="upd-field">
                                    <span className="upd-field-label">ULW</span>
                                    <span className="upd-field-static">{formData.ULW}</span>
                                </div>
                            </div>
                        </div>

                        {/* Personal details */}
                        <div className="upd-section">
                            <p className="upd-section-title">Personal Details</p>
                            <div className="upd-fields-grid">
                                <div className="upd-field">
                                    <span className="upd-field-label">Customer Name</span>
                                    <span className="upd-field-static">{formData.CustomerName}</span>
                                </div>
                                <div className="upd-field">
                                    <span className="upd-field-label">Driver Name</span>
                                    <span className="upd-field-static">{formData.driverName}</span>
                                </div>
                                <div className="upd-field">
                                    <span className="upd-field-label">Contact No.</span>
                                    <span className="upd-field-static">{formData.driverNumber}</span>
                                </div>
                                <div className="upd-field">
                                    <span className="upd-field-label">Email</span>
                                    <span className="upd-field-static truncate">{formData.driverEmail}</span>
                                </div>

                                <div className="upd-doc-tile">
                                    <span className="upd-field-label">Pan</span>
                                    <img className="upd-doc-image" src={formData.panCard} alt="" />
                                </div>
                                <div className="upd-doc-tile">
                                    <span className="upd-field-label">Aadhar Card</span>
                                    <img className="upd-doc-image" src={formData.adharCard} alt="" />
                                </div>
                            </div>
                        </div>

                    </div>
                </>
            )}

            {/* VC block */}    {  /*   has error with cusomer profile image  */}
            {userId?.startsWith("VC-") && (
                <>
                    <ProfileHeader
                        name={formData.vendorName}
                        email={formData.email}
                        showBack={state?.frompage === 'craneuserprofile'}
                        handleBack={handleBack}
                        isEditMode={isEditMode}
                        handleEditToggle={handleEditToggle}
                        isLoading={isLoading}
                        avatarSrc={profileFile || formData.profileImage}
                        handleProfileInputRefClick={handleProfileInputRefClick}
                        profileInputRef={profileInputRef}
                        handleChange={handleChange}
                        tabs={tabs}
                        activeTab={activeTab}
                        setActiveTab={setActiveTab}
                    />

                    <div className="upd-content">

                        {/* Vehicle details */}
                        <div className="upd-section">
                            <p className="upd-section-title">Vehicle Details</p>
                            <div className="upd-fields-grid">
                                <div className="upd-field">
                                    <span className="upd-field-label">Vehicle No</span>
                                    <span className="upd-field-static">{formData.vehicleNo || 'N/A'}</span>
                                </div>
                                <div className="upd-field">
                                    <span className="upd-field-label">Chassis No</span>
                                    <span className="upd-field-static">{formData.chassisNo}</span>
                                </div>
                                <div className="upd-field">
                                    <span className="upd-field-label">Engine No</span>
                                    <span className="upd-field-static">{formData.engineNo}</span>
                                </div>
                                <div className="upd-field">
                                    <span className="upd-field-label">Make</span>
                                    <span className="upd-field-static">{formData.make}</span>
                                </div>
                                <div className="upd-field">
                                    <span className="upd-field-label">Model</span>
                                    <span className="upd-field-static">{formData.model}</span>
                                </div>
                                <div className="upd-field">
                                    <span className="upd-field-label">Year</span>
                                    <span className="upd-field-static">{formData.year}</span>
                                </div>
                                <div className="upd-field">
                                    <span className="upd-field-label">Type</span>
                                    <span className="upd-field-static">{formData.type}</span>
                                </div>
                                <div className="upd-field">
                                    <span className="upd-field-label">Application No</span>
                                    <span className="upd-field-static">{formData.application}</span>
                                </div>
                                <div className="upd-field">
                                    <span className="upd-field-label">Insurance</span>
                                    <span className="upd-field-static">{formData.InsuranceName}</span>
                                </div>
                                <div className="upd-field">
                                    <span className="upd-field-label">GVW</span>
                                    <span className="upd-field-static">{formData.GVW}</span>
                                </div>
                                <div className="upd-field">
                                    <span className="upd-field-label">ULW</span>
                                    <span className="upd-field-static">{formData.ULW}</span>
                                </div>
                            </div>
                        </div>

                        {/* Personal details */}
                        <div className="upd-section">
                            <p className="upd-section-title">Personal Details</p>
                            <div className="upd-fields-grid">
                                <div className="upd-field">
                                    <span className="upd-field-label">Vendor Name</span>
                                    <span className="upd-field-static">{formData.vendorName}</span>
                                </div>
                                <div className="upd-field">
                                    <span className="upd-field-label">Driver Name</span>
                                    <span className="upd-field-static">{formData.contactPerson}</span>
                                </div>
                                <div className="upd-field">
                                    <span className="upd-field-label">Contact No.</span>
                                    <span className="upd-field-static">{formData.vendorPhone}</span>
                                </div>
                                <div className="upd-field">
                                    <span className="upd-field-label">Email</span>
                                    <CustomInput
                                        isEditable={isEditMode}
                                        value={formData.email}
                                        placeholder='Enter email'
                                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleSubmit(e); } }}
                                    ></CustomInput>
                                </div>

                                <div className="upd-doc-tile">
                                    <span className="upd-field-label">Pan Card</span>
                                    <img
                                        hidden={formData.panCard === "" || formData.panCard === "Pan Value"}
                                        className="upd-doc-image"
                                        src={panFile || formData.panCard}
                                        alt=""
                                        onClick={handlePanInputRefClick}
                                    />
                                    <input
                                        hidden={!(formData.panCard === "" || formData.panCard === "Pan Value")}
                                        ref={panInputRef}
                                        type="file"
                                        name="panCard"
                                        onChange={handleChange}
                                        accept="image/*"
                                        className="form-control"
                                        required
                                    />
                                </div>

                                <div className="upd-doc-tile">
                                    <span className="upd-field-label">Aadhar Card</span>
                                    <img
                                        hidden={formData.adharCard === "" || formData.adharCard === "Adhar Value"}
                                        className="upd-doc-image"
                                        src={aadharFile || formData.adharCard}
                                        alt=""
                                        onClick={handleAdharInputRefClick}
                                    />
                                    <input
                                        hidden={!(formData.adharCard === "" || formData.adharCard === "Adhar Value")}
                                        ref={aadharInputRef}
                                        type="file"
                                        name="adharCard"
                                        onChange={handleChange}
                                        accept="image/*"
                                        className="form-control"
                                        required
                                    />
                                </div>
                            </div>
                        </div>

                    </div>
                </>
            )}

            {/* VED block */}
            {userId?.startsWith("VED-") && (
                <>
                    <ProfileHeader
                        name={formData.vendorName}
                        email={formData.driverEmail}
                        showBack={state?.frompage === 'craneuserprofile'}
                        handleBack={handleBack}
                        isEditMode={isEditMode}
                        handleEditToggle={handleEditToggle}
                        isLoading={isLoading}
                        avatarSrc={profileFile || formData.profileImage}
                        handleProfileInputRefClick={handleProfileInputRefClick}
                        profileInputRef={profileInputRef}
                        handleChange={handleChange}
                        tabs={tabs}
                        activeTab={activeTab}
                        setActiveTab={setActiveTab}
                    />

                    <div className="upd-content">

                        {/* Personal details */}
                        <div className="upd-section">
                            <p className="upd-section-title">Personal Details</p>
                            <div className="upd-fields-grid">
                                <div className="upd-field">
                                    <span className="upd-field-label">Vendor Name</span>
                                    <CustomInput
                                        isEditable={isEditMode}
                                        value={formData.vendorName}
                                        placeholder='Enter name'
                                        onChange={(e) => setFormData({ ...formData, vendorName: e.target.value })}
                                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleSubmit(e); } }}
                                    ></CustomInput>
                                </div>
                                <div className="upd-field">
                                    <span className="upd-field-label">Driver Name</span>
                                    <CustomInput
                                        isEditable={isEditMode}
                                        value={formData.driverName}
                                        placeholder='Enter name'
                                        onChange={(e) => setFormData({ ...formData, driverName: e.target.value })}
                                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleSubmit(e); } }}
                                    ></CustomInput>
                                </div>
                                <div className="upd-field">
                                    <span className="upd-field-label">Contact No.</span>
                                    <CustomInput
                                        isEditable={isEditMode}
                                        value={formData.driverNumber}
                                        placeholder='Enter number'
                                        onChange={(e) => setFormData({ ...formData, driverNumber: e.target.value })}
                                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleSubmit(e); } }}
                                    ></CustomInput>
                                </div>
                                <div className="upd-field">
                                    <span className="upd-field-label">Email</span>
                                    <CustomInput
                                        isEditable={isEditMode}
                                        value={formData.driverEmail}
                                        placeholder='Enter email'
                                        onChange={(e) => setFormData({ ...formData, driverEmail: e.target.value })}
                                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleSubmit(e); } }}
                                    ></CustomInput>
                                </div>

                                <div className="upd-doc-tile">
                                    <span className="upd-field-label">Pan Card</span>
                                    <img
                                        hidden={formData.panCard === "" || formData.panCard === "Pan Value"}
                                        className="upd-doc-image"
                                        src={panFile || formData.panCard}
                                        alt=""
                                        onClick={handlePanInputRefClick}
                                    />
                                    <input
                                        hidden={!(formData.panCard === "" || formData.panCard === "Pan Value")}
                                        ref={panInputRef}
                                        type="file"
                                        name="panCard"
                                        onChange={handleChange}
                                        accept="image/*"
                                        className="form-control"
                                        required
                                    />
                                </div>

                                <div className="upd-doc-tile">
                                    <span className="upd-field-label">Aadhar Card</span>
                                    <img
                                        hidden={formData.adharCard === "" || formData.adharCard === "Adhar Value"}
                                        className="upd-doc-image"
                                        src={aadharFile || formData.adharCard}
                                        alt=""
                                        onClick={handleAdharInputRefClick}
                                    />
                                    <input
                                        hidden={!(formData.adharCard === "" || formData.adharCard === "Adhar Value")}
                                        ref={aadharInputRef}
                                        type="file"
                                        name="adharCard"
                                        onChange={handleChange}
                                        accept="image/*"
                                        className="form-control"
                                        required
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Vehicle details */}
                        <div className="upd-section">
                            <p className="upd-section-title">Vehicle Details</p>
                            <div className="upd-fields-grid">
                                <div className="upd-field">
                                    <span className="upd-field-label">Vehicle No</span>
                                    <CustomInput
                                        isEditable={isEditMode}
                                        value={formData.vehicleNo}
                                        placeholder='Enter number'
                                        onChange={(e) => setFormData({ ...formData, vehicleNo: e.target.value })}
                                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleSubmit(e); } }}
                                    ></CustomInput>
                                </div>
                                <div className="upd-field">
                                    <span className="upd-field-label">Chassis No</span>
                                    <span className="upd-field-static">{formData.chassisNo}</span>
                                </div>
                                <div className="upd-field">
                                    <span className="upd-field-label">Engine No</span>
                                    <span className="upd-field-static">{formData.engineNo}</span>
                                </div>
                                <div className="upd-field">
                                    <span className="upd-field-label">Make</span>
                                    <span className="upd-field-static">{formData.make}</span>
                                </div>
                                <div className="upd-field">
                                    <span className="upd-field-label">Model</span>
                                    <span className="upd-field-static">{formData.model}</span>
                                </div>
                                <div className="upd-field">
                                    <span className="upd-field-label">Year</span>
                                    <span className="upd-field-static">{formData.year}</span>
                                </div>
                                <div className="upd-field">
                                    <span className="upd-field-label">Type</span>
                                    <span className="upd-field-static">{formData.type}</span>
                                </div>
                                <div className="upd-field">
                                    <span className="upd-field-label">Application No</span>
                                    <span className="upd-field-static">{formData.application}</span>
                                </div>
                                <div className="upd-field">
                                    <span className="upd-field-label">Insurance</span>
                                    <span className="upd-field-static">{formData.InsuranceName}</span>
                                </div>
                                <div className="upd-field">
                                    <span className="upd-field-label">GVW</span>
                                    <span className="upd-field-static">{formData.GVW}</span>
                                </div>
                            </div>
                        </div>

                    </div>
                </>
            )}

            <ToastContainer />
        </div>
    );
};

export default UserProfileDetails;